// PowerSync backend connector (local-first migration, Phase 3).
//
// fetchCredentials → exchanges the app session for a short-lived sync token.
// uploadData       → drains the local write queue to PUT /api/sync/upload,
//                    one transaction at a time, in the {batch:[...]} shape the
//                    backend write connector (services/syncWrite.js) expects.
import { useAuthStore } from '../../stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const POWERSYNC_URL_FALLBACK = import.meta.env.VITE_POWERSYNC_URL || '';

function authHeaders() {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class MissionControlConnector {
  async fetchCredentials() {
    const res = await fetch(`${API_BASE}/sync/token`, { headers: authHeaders() });
    if (!res.ok) {
      // 401 → not logged in; returning null tells PowerSync to stay disconnected.
      if (res.status === 401) return null;
      throw new Error(`fetchCredentials failed: ${res.status}`);
    }
    const { token, powersync_url } = await res.json();
    const endpoint = powersync_url || POWERSYNC_URL_FALLBACK;
    if (!endpoint) throw new Error('No PowerSync endpoint configured');
    return { endpoint, token };
  }

  async uploadData(database) {
    // One transaction at a time preserves the client's intended atomicity and
    // ordering. Loop until the queue is drained.
    for (;;) {
      const tx = await database.getNextCrudTransaction();
      if (!tx) break;

      const batch = tx.crud.map((entry) => ({
        op: entry.op,          // 'PUT' | 'PATCH' | 'DELETE'
        table: entry.table,
        id: entry.id,
        data: entry.opData,    // changed columns (PUT/PATCH); undefined for DELETE
      }));

      const res = await fetch(`${API_BASE}/sync/upload`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ batch }),
      });

      if (!res.ok) {
        // 5xx → transient; throw so PowerSync keeps the queue and retries with
        // backoff. (Per-op validation errors come back 2xx and are dropped by
        // the backend, so they won't wedge us here.)
        throw new Error(`uploadData failed: ${res.status}`);
      }

      await tx.complete();
    }
  }
}
